import { adminReopen } from '../state/admin';
import { editSheet, returnFor } from '../state/adminUi';
import type { CallView } from '../state/schedule';
import type { CompletionRec } from '../state/completions';
import { parked, reopenMissed, uploadPhotos } from '../state/completions';
import { reminders } from '../state/reminders';
import { fmtShortDate } from '../lib/dates';
import { toast } from '../state/toast';
import { PhotoThumbs } from './PhotoThumbs';

const digits = (phone: string) => phone.replace(/\D/g, '');

export interface AdminCardActions {
  weekOf: string;
  day: string;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
}

interface Props {
  call: CallView;
  rec: CompletionRec | null;
  onDone: () => void;
  onMissed: () => void;
  onReschedule: () => void;
  /** Present only when the session is admin — carries the day context for edits. */
  admin?: AdminCardActions;
}

/** One service call. Active cards carry the big Done/Missed actions; resolved cards
 *  stay readable in sunlight (tinted, not dimmed — v1 lesson) and sink to the bottom. */
export function CallCard({ call, rec, onDone, onMissed, onReschedule, admin }: Props) {
  const isParked = parked.value.has(call.id);
  const reminder = admin ? reminders.value[call.id] : undefined;
  const status = rec?.status;
  const tone =
    status === 'done'
      ? 'bg-done-tint border-emerald-100'
      : status === 'missed'
        ? 'bg-missed-tint border-red-100'
        : 'bg-white border-slate-200';

  async function addPhotos(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = [...(input.files ?? [])];
    input.value = '';
    if (!files.length) return;
    toast('Uploading…');
    await uploadPhotos(call.id, files);
  }

  return (
    // The 3px left accent is v1's own card signature (kept intentionally for crew
    // familiarity) — classified as a false positive against the design hook.
    <article
      class={`rounded-xl border ${tone} border-l-[3px] border-l-brand-600 p-3 shadow-sm`}
      data-testid="call-card"
      data-call-id={call.id}
      data-status={status ?? 'active'}
    >
      <div class="flex items-start gap-2">
        {call.slot && (
          <span
            class={`shrink-0 rounded-lg px-2 py-1 text-xs font-bold ${status === 'missed' ? 'bg-missed-ink text-white' : 'bg-navy-900 text-white'}`}
          >
            {call.slot}
          </span>
        )}
        <div class="min-w-0">
          <h3 class="truncate font-bold text-navy-900">
            {call.development}
            {call.lot && <span class="font-semibold text-slate-500"> · Lot {call.lot}</span>}
          </h3>
          {call.address && (
            <a
              class="text-sm font-medium text-brand-700 underline-offset-2 hover:underline"
              href={`https://maps.apple.com/?q=${encodeURIComponent(call.address)}`}
              target="_blank"
              rel="noreferrer"
            >
              {call.address}
            </a>
          )}
        </div>
        {call.tech && (
          <span class="ml-auto shrink-0 rounded-full bg-sky-100 px-2 py-1 text-xs font-bold text-sky-800">
            {call.tech}
          </span>
        )}
      </div>

      {call.problem && <p class="mt-2 text-[15px] leading-snug text-navy-900">{call.problem}</p>}

      {(call.name || digits(call.phone).length >= 7) && (
        <div class="mt-2 flex items-center gap-2">
          {call.name && <span class="text-sm text-slate-600">{call.name}</span>}
          {digits(call.phone).length >= 7 && (
            <a
              class="tap ml-auto flex h-11 items-center gap-1.5 rounded-xl bg-emerald-600 px-3 font-semibold text-white"
              href={`tel:${digits(call.phone)}`}
            >
              <svg viewBox="0 0 24 24" class="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M6.6 10.8a15.5 15.5 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.8 21 3 13.2 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z" />
              </svg>
              Call
            </a>
          )}
        </div>
      )}

      {call.instructions && (
        <p class="mt-2 rounded-lg bg-note-amber px-3 py-2 text-sm font-medium text-note-ink">
          {call.instructions}
        </p>
      )}

      {isParked && (
        <p class="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-600">
          On hold — the office is handling this one.
        </p>
      )}

      {!status && (!isParked || admin) && (
        <div class="mt-3 flex gap-2">
          <button class="btn-primary flex-1" data-testid="btn-done" onClick={onDone}>
            Done
          </button>
          <button class="btn-danger-outline" data-testid="btn-missed" onClick={onMissed}>
            Missed
          </button>
        </div>
      )}

      {rec && (
        <div class="mt-2 border-t border-black/5 pt-2 text-sm">
          <p class={`font-semibold ${status === 'missed' ? 'text-missed-ink' : 'text-done-ink'}`}>
            {status === 'missed' ? 'MISSED' : 'Done'}
            {rec.by && ` — ${rec.by}`}
            {rec.at &&
              ` · ${new Date(rec.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
            {rec.needsReturn && (
              <span class="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-missed-ink">
                needs follow-up
              </span>
            )}
          </p>
          {rec.note && <p class="mt-1 text-slate-600">{rec.note}</p>}
          <PhotoThumbs keys={rec.photos} />
          <div class="mt-2 flex gap-2">
            {status === 'missed' && (
              <button
                class="btn-ghost flex-1"
                onClick={async () => {
                  if (await reopenMissed(call.id)) onReschedule();
                }}
              >
                Reschedule
              </button>
            )}
            <label class="btn-ghost flex flex-1 items-center justify-center gap-1.5">
              <svg
                viewBox="0 0 24 24"
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path d="M3 8h3l2-3h8l2 3h3v11H3z" stroke-linejoin="round" />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
              Photo
              <input type="file" accept="image/*" multiple class="sr-only" onChange={addPhotos} />
            </label>
            {admin && (
              <button
                class="btn-ghost flex-1"
                data-testid="btn-admin-reopen"
                onClick={async () => {
                  if (!confirm('Reopen this job? The completion record moves to history.')) return;
                  if (await adminReopen(call.id)) toast('Reopened.');
                }}
              >
                Reopen
              </button>
            )}
          </div>
        </div>
      )}

      {admin && (
        <div
          class="mt-2 flex items-center gap-1.5 border-t border-black/5 pt-2"
          data-testid="admin-row"
        >
          <button class="mini-btn" aria-label="Move up" onClick={() => admin.onMove(-1)}>
            ↑
          </button>
          <button class="mini-btn" aria-label="Move down" onClick={() => admin.onMove(1)}>
            ↓
          </button>
          <button
            class={`mini-btn ${reminder ? 'border-note-ink/30 bg-note-amber text-note-ink' : ''}`}
            data-testid="btn-return"
            onClick={() =>
              (returnFor.value = {
                id: call.id,
                development: call.development,
                address: call.address,
              })
            }
          >
            <svg
              viewBox="0 0 24 24"
              class="mr-1 inline h-3.5 w-3.5"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
            </svg>
            {reminder?.date ? fmtShortDate(reminder.date) : 'Return'}
          </button>
          <button
            class="mini-btn ml-auto"
            data-testid="btn-edit-call"
            onClick={() =>
              (editSheet.value = {
                mode: 'edit',
                id: call.id,
                call,
                day: admin.day,
                weekOf: admin.weekOf,
                fromDb: false,
              })
            }
          >
            Edit
          </button>
          <button
            class="mini-btn text-missed-ink"
            data-testid="btn-delete-call"
            onClick={admin.onDelete}
          >
            Delete
          </button>
        </div>
      )}
    </article>
  );
}
